import type { MenuCard, MenuGroup } from './header-data';
import type { Benefit } from '@eliadmin/core/src/components/svetech/ui/benefits';

import Link from '@eliadmin/core/src/components/link';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Benefits } from '@eliadmin/core/src/components/svetech/ui/benefits';
import { CmsImage } from '@eliadmin/core/src/components/svetech/ui/cms-image';
import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { iconTileClassName } from '@eliadmin/core/src/components/svetech/ui/icon-tile';

/** Up to six cards: 3 columns with the photo beside the text, more: 4 columns with the photo on top. */
const WIDE_LAYOUT_FROM = 7;

export function MegaMenuPanel({ group, benefits }: { group: MenuGroup; benefits: Benefit[] }) {
  const wide = group.cards.length >= WIDE_LAYOUT_FROM;

  return (
    <div className="flex gap-12">
      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <div className="flex items-center justify-between gap-6">
          <p className="svt-eyebrow text-[#2c7a25]">{group.name}</p>
          {group.href && group.linkLabel ? (
            <Link
              href={group.href}
              prefetch={false}
              className="svt-button flex items-center gap-2 text-[#2c7a25] hover:text-[#123410]"
            >
              {group.linkLabel}
              <Icon name="arrow-right" className="size-5" />
            </Link>
          ) : null}
        </div>
        <ul className={cn('grid gap-2', wide ? 'grid-cols-4' : 'grid-cols-3')}>
          {group.cards.map(card => (
            <li key={card.id} className="flex">
              <MenuCardItem card={card} wide={wide} />
            </li>
          ))}
        </ul>
        <div className="h-px w-full bg-[#e6e8e2]" />
        <Benefits items={benefits} />
      </div>
      <PromoCard promo={group.promo} />
    </div>
  );
}

function MenuCardItem({ card, wide }: { card: MenuCard; wide: boolean }) {
  const className = cn(
    'flex w-full gap-3 rounded-[20px] rounded-tr-none p-4 transition-colors',
    wide ? 'flex-col' : 'gap-4',
    card.href &&
      'hover:bg-[#f2f3ef] focus-visible:bg-[#f2f3ef] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25]',
  );
  const content = (
    <>
      <span
        className={cn(
          'relative block shrink-0 overflow-hidden rounded-[15px] rounded-tr-none bg-[#f2f3ef]',
          wide ? 'size-14' : 'size-16',
        )}
      >
        <CmsImage image={card.image} decorative sizes="64px" />
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="svt-title-menu text-[#14171a]">{card.title}</span>
        {card.description ? (
          <span className="svt-body-s text-[#4a5560]">{card.description}</span>
        ) : null}
      </span>
    </>
  );

  return card.href ? (
    <Link href={card.href} prefetch={false} className={className}>
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
}

function PromoCard({ promo }: { promo: MenuGroup['promo'] }) {
  if (!promo.title) return null;

  const content = (
    <>
      <CmsImage image={promo.image} decorative sizes="400px" />
      <span
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-[250px] bg-gradient-to-b from-transparent to-black/80 backdrop-blur-[5px]"
      />
      <span className={iconTileClassName('accent', 'relative text-[#14171a]')}>
        <Icon name="arrow-up-right" className="size-6" />
      </span>
      <div className="relative flex w-full flex-col gap-2">
        <Heading tag="h3" as="p" tone="dark" title={promo.title} />
        {promo.text ? <p className="svt-body text-[#bcc0c3]">{promo.text}</p> : null}
      </div>
    </>
  );
  const className =
    'relative flex w-[400px] shrink-0 flex-col items-end justify-between overflow-hidden rounded-[32px] rounded-tr-none bg-[#14171a] p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25] focus-visible:ring-offset-2';

  return promo.href ? (
    <Link href={promo.href} prefetch={false} className={className}>
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
}
